import React, { useState } from 'react';
import { Autocomplete, ConfirmationModal, RadioButtonWithLabel, Spinner, Tooltip, Typography, useOverlayContext } from 'seagull';
import styles from "./GlobalBillModal.module.scss";
import { Controller, useForm } from 'react-hook-form';
import { useStoreActions } from "../../contexts";
import { handleRequest } from "../../infra/utils";
import { getServiceOptionSelected, getServiceOptionLabel, mapServicesToServiceOptions } from "./GlobalBillModal.utils";
import { requiredValidation } from 'validators';
import ContactsSelector from "./ContactsSelector/ContactsSelector";
import { genericConnector } from "../../utils/react-hook-form.utils";
import { BillingOption, GlobalBillModalErrorMessages } from "./GlobalBillModal.types";
import { useFetchContactAgreements } from "../../hooks/useGetAgreements";
import { getInvoiceDateInfo } from "../../pages/AgreementMinisite/BillingHub/BillingHub.utils";
import { getOptionLabel, getOptionSelected } from 'seagull';
import { getGlobalBillServiceOptions } from "../../business-logic/billServiceSelector";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
export default function GlobalBillModal(_ref) {
  let {
    dismissModal,
    handleOpenAddCharge,
    handleOpenBillService
  } = _ref;
  const {
    showErrorToast
  } = useOverlayContext();
  const {
    api
  } = useStoreActions();
  const {
    fetchAgreements,
    agreements,
    isOnlyOneAgreement,
    loading
  } = useFetchContactAgreements();
  const [selectedCheckboxOption, setSelectedCheckboxOption] = useState(BillingOption.outOfScope);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [globalBillCommonData, setGlobalBillCommonData] = useState(null);
  const [isCommonDataLoading, setIsCommonDataLoading] = useState(false);
  const overlayContext = useOverlayContext();
  const {
    BILL_CLIENT_MULTIPLE_LINES
  } = useFeatureFlags();
  const {
    control,
    watch,
    setValue,
    handleSubmit
  } = useForm({
    mode: 'onSubmit'
  });
  const [selectedService, selectedAgreement, selectedContact] = watch(['selectedService', 'selectedAgreement', 'selectedContactId']);
  const showServicesSelector = selectedCheckboxOption === BillingOption.inAgreement && selectedAgreement && selectedContact;
  const handleRequestRelationship = async selectedAgreementId => {
    setIsCommonDataLoading(true);
    const [relationship, controlData, dates] = await Promise.all([handleRequest(api.relationship.get(selectedAgreementId)), handleRequest(api.relationship.getBillingControlData(selectedAgreementId)), handleRequest(api.invoiceDraft.getDates(selectedAgreementId))]);
    if (!relationship.success || !controlData.success || !dates.success) {
      showErrorToast(GlobalBillModalErrorMessages.FailedToRequestServices, 'global-bill-failed-to-fetch');
      return;
    }
    setGlobalBillCommonData({
      relationship: relationship.data,
      controlData: controlData.data,
      dates: dates.data
    });
    setServiceOptions(BILL_CLIENT_MULTIPLE_LINES ? getGlobalBillServiceOptions({
      services: relationship.data.services,
      controlData: controlData.data
    }) : mapServicesToServiceOptions(relationship.data.services, controlData.data));
    setIsCommonDataLoading(false);
  };
  const handleChangeAgreement = agreementId => {
    if (agreementId) {
      handleRequestRelationship(agreementId);
    }
  };
  const onClearAgreement = () => {
    setSelectedCheckboxOption(BillingOption.outOfScope);
    setValue('selectedService', null, {
      shouldDirty: false
    });
    setServiceOptions([]);
  };
  const handleChangeContact = contactId => {
    onClearAgreement();
    setValue('selectedAgreement', null);
    if (contactId) {
      fetchAgreements(contactId).then(agreements => {
        if ((agreements === null || agreements === void 0 ? void 0 : agreements.length) === 1) {
          setValue('selectedAgreement', agreements[0]);
          handleChangeAgreement(agreements[0].value);
        }
      });
    }
  };
  const onOpenAddCharge = async globalBillCommonData => {
    const {
      relationship,
      controlData,
      dates
    } = globalBillCommonData;
    const selectedDate = dates.dates().length > 0 ? dates.dates()[0] : null;
    const invoiceDateInfo = getInvoiceDateInfo(selectedDate, dates);
    const context = {
      relationship: relationship,
      totalCredits: controlData.totalCredits,
      invoiceDateInfo: invoiceDateInfo,
      earliestBillableDate: dates.earliestBillableDate(),
      manualServicesBilling: controlData.manualServices,
      controlData: controlData
    };
    handleOpenAddCharge(context);
  };
  const onOpenBillService = async (globalBillCommonData, service) => {
    const {
      relationship,
      controlData,
      dates
    } = globalBillCommonData;
    const selectedDate = dates.dates().length > 0 ? dates.dates()[0] : null;
    const invoiceDateInfo = getInvoiceDateInfo(selectedDate, dates);
    const billServiceData = {
      relationship: relationship,
      serviceWithAmendments: service,
      totalCredits: controlData.totalCredits,
      manualServicesBilling: controlData.manualServices,
      invoiceDateInfo: invoiceDateInfo,
      earliestBillableDate: dates.earliestBillableDate(),
      controlData: controlData
    };
    handleOpenBillService(billServiceData);
  };
  const handleGlobalBillModalSubmit = () => {
    const selectedServiceId = selectedService === null || selectedService === void 0 ? void 0 : selectedService.id;
    const service = globalBillCommonData === null || globalBillCommonData === void 0 ? void 0 : globalBillCommonData.relationship.services.find(s => s.id === selectedServiceId);
    if (agreements.length === 0) {
      showErrorToast('This client currently has no agreement with you.', 'global-bill-modal-no-agreements-error-toast');
      return;
    }
    if (selectedCheckboxOption === BillingOption.outOfScope && globalBillCommonData) {
      onOpenAddCharge(globalBillCommonData);
      dismissModal();
    } else if (selectedCheckboxOption === BillingOption.inAgreement && globalBillCommonData && service) {
      onOpenBillService(globalBillCommonData, service);
      dismissModal();
    } else {
      overlayContext.showErrorToast('Failed to open bill service modal', 'open-bill-service-modal-error');
    }
  };
  const handleChooseAgreement = newSelectedAgreement => {
    onClearAgreement();
    if (newSelectedAgreement === selectedAgreement) {
      return;
    }
    if (newSelectedAgreement != null) {
      handleRequestRelationship(newSelectedAgreement.value);
    }
  };
  const handleChooseBillingOption = value => {
    setValue('selectedService', null);
    setSelectedCheckboxOption(value);
  };
  function renderAgreementsField() {
    return loading ? /*#__PURE__*/React.createElement(Spinner, {
      size: 32,
      centerBoxHeight: 36,
      centeredInBox: true
    }) : agreements.length > 1 ? /*#__PURE__*/React.createElement(Controller, {
      control: control,
      name: "selectedAgreement",
      rules: requiredValidation(true),
      render: renderProps => /*#__PURE__*/React.createElement(Autocomplete, {
        selectOnFocus: true,
        fullWidth: true,
        "data-testid": "global-bill-modal-agreements-selector",
        textFieldProps: {
          label: 'Agreement',
          error: genericConnector(renderProps).showErrors
        },
        value: renderProps.field.value,
        options: agreements,
        getOptionLabel: getOptionLabel,
        getOptionSelected: getOptionSelected,
        onChange: (event, value) => {
          handleChooseAgreement(value);
          renderProps.field.onChange(value);
        }
      })
    }) : isOnlyOneAgreement ? null : /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "global-bill-modal-no-agreements",
      className: styles.noAgreementsText,
      variant: "paragraph1",
      skin: "secondary"
    }, "This client currently has no agreement with you.");
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ConfirmationModal, {
    title: "Bill client",
    "data-testid": "global-bill-modal",
    primaryButtonTestid: "global-bill-modal-continue-button",
    textAlignCenter: false,
    onCloseButtonClick: dismissModal,
    primaryButtonLabel: "Continue",
    secondaryButtonLabel: "Cancel",
    onSecondaryButtonClick: dismissModal,
    primaryButtonLoading: isCommonDataLoading,
    onPrimaryButtonClick: handleSubmit(handleGlobalBillModalSubmit),
    content: /*#__PURE__*/React.createElement("div", {
      className: styles.globalBillModalContent
    }, /*#__PURE__*/React.createElement(Controller, {
      control: control,
      name: "selectedContactId",
      rules: requiredValidation(true),
      render: renderProps => /*#__PURE__*/React.createElement(ContactsSelector, {
        disabled: loading,
        selectedContactId: renderProps.field.value,
        error: genericConnector(renderProps).showErrors,
        onChange: value => {
          renderProps.field.onChange(value);
          handleChangeContact(value);
        }
      })
    }), selectedContact && renderAgreementsField(), /*#__PURE__*/React.createElement("div", null, selectedAgreement && /*#__PURE__*/React.createElement("div", {
      "data-testid": "global-bill-radio-buttons",
      className: styles.billingSelectorsWrapper
    }, /*#__PURE__*/React.createElement(RadioButtonWithLabel, {
      testId: "out-of-scope-radio",
      label: 'Bill an out of scope charge',
      checked: selectedCheckboxOption === BillingOption.outOfScope,
      onChange: () => handleChooseBillingOption(BillingOption.outOfScope)
    }), /*#__PURE__*/React.createElement(RadioButtonWithLabel, {
      testId: "in-agreement-radio",
      label: 'Bill a service listed in the agreement',
      checked: selectedCheckboxOption === BillingOption.inAgreement,
      onChange: () => handleChooseBillingOption(BillingOption.inAgreement)
    })), showServicesSelector && /*#__PURE__*/React.createElement(Controller, {
      control: control,
      name: "selectedService",
      rules: requiredValidation(true),
      render: renderProps => /*#__PURE__*/React.createElement(Autocomplete, {
        className: styles.manualServicesSelector,
        selectOnFocus: true,
        "data-testid": "agreement-manual-services-selector",
        textFieldProps: {
          placeholder: 'Choose a service to bill',
          error: !!renderProps.fieldState.error
        },
        value: renderProps.field.value,
        getOptionSelected: getServiceOptionSelected,
        getOptionLabel: getServiceOptionLabel,
        options: serviceOptions,
        onBlur: renderProps.field.onBlur,
        noOptionsText: "No manual services available",
        getOptionDisabled: option => option.disabledReason.isDisabled,
        renderOption: option => option.disabledReason.isDisabled ? /*#__PURE__*/React.createElement(Tooltip, {
          placement: "right",
          title: option.disabledReason.reasonTooltip
        }, /*#__PURE__*/React.createElement("div", {
          className: styles.serviceOptionContent
        }, /*#__PURE__*/React.createElement(Typography, {
          variant: "paragraph1"
        }, option.name), /*#__PURE__*/React.createElement(Typography, {
          variant: "paragraph1",
          skin: "secondary"
        }, option.costString))) : /*#__PURE__*/React.createElement("div", {
          className: styles.serviceOptionContent
        }, /*#__PURE__*/React.createElement(Typography, {
          variant: "paragraph1"
        }, option.name), /*#__PURE__*/React.createElement(Typography, {
          variant: "paragraph1",
          skin: "secondary"
        }, option.costString)),
        loading: isCommonDataLoading,
        onChange: (event, value) => {
          const service = value ? value : null;
          if (service === selectedService) {
            return;
          }
          renderProps.field.onChange(service);
        },
        onInputChange: (e, value) => {
          const service = serviceOptions.find(option => option.name === value);
          if (service) {
            renderProps.field.onChange(service);
          }
        }
      })
    })))
  }));
}